import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { getBases, createBase, updateBase, deleteBase, getMuestrasBase, getTizados, getFichas, uploadBaseFile, createFicha, deleteFicha, createTizado, deleteTizado, getMarcas, getTiposProducto, getEntalles, getTelas, getHilos } from '../../services/muestrasApi';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, X, Upload, Download, Eye, FileText, Scissors,
  CheckCircle2, XCircle, Clock, Search, Filter, AlertTriangle, ChevronUp, ChevronDown, RotateCcw
} from 'lucide-react';
import { FileCell, ImageCell, StatusBadge } from '../../components/muestras/TableCells';
import { useResizableColumns, ResizableHeader } from '../../components/muestras/ResizableTable';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Definir columnas con anchos iniciales para Bases
const INITIAL_BASES_COLUMNS = [
  { key: 'muestra', header: 'Muestra Base', width: 280, minWidth: 150 },
  { key: 'patron', header: 'Patrón', width: 130, minWidth: 90 },
  { key: 'imagen', header: 'Imagen', width: 100, minWidth: 80 },
  { key: 'fichas', header: 'Fichas', width: 90, minWidth: 70 },
  { key: 'tizados', header: 'Tizados', width: 90, minWidth: 70 },
  { key: 'estado', header: 'Estado', width: 90, minWidth: 70 },
  { key: 'acciones', header: 'Acciones', width: 100, minWidth: 80 },
];

// ============================================================
// MODAL DE FICHAS
// ============================================================
const FichasModal = ({ baseId, fichas: initialFichas, onClose, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nombre_ficha: '' });
  const [archivoFile, setArchivoFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localFichas, setLocalFichas] = useState(initialFichas.filter(f => f.id_base === baseId));
  const { token } = useApp();

  // Sincronizar fichas locales cuando cambian las props
  useEffect(() => {
    setLocalFichas(initialFichas.filter(f => f.id_base === baseId));
  }, [initialFichas, baseId]);

  const filteredFichas = searchQuery 
    ? localFichas.filter(f => f.nombre_ficha?.toLowerCase().includes(searchQuery.toLowerCase()))
    : localFichas;

  const handleSubmit = async () => {
    if (!formData.nombre_ficha) { toast.error('El nombre es requerido'); return; }
    setSubmitting(true);
    try {
      let filename = null;
      if (archivoFile) {
        const uploadData = new FormData();
        uploadData.append('file', archivoFile);
        const res = await fetch(`${API_BASE}/api/muestras/upload?folder=fichas`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: uploadData
        });
        if (res.ok) {
          const data = await res.json();
          filename = data.filename;
        }
      }
      const newFicha = await createFicha({ id_base: baseId, nombre_ficha: formData.nombre_ficha, archivo: filename });
      toast.success('Ficha creada');
      // Actualizar estado local inmediatamente
      setLocalFichas(prev => [...prev, newFicha]);
      setFormData({ nombre_ficha: '' });
      setArchivoFile(null);
      setShowForm(false);
      onUpdate();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta ficha?')) return;
    try {
      await deleteFicha(id);
      toast.success('Ficha eliminada');
      // Actualizar estado local inmediatamente
      setLocalFichas(prev => prev.filter(f => f.id_ficha !== id));
      onUpdate();
    } catch (error) {
      console.error('Error al eliminar ficha:', error);
      toast.error(error.message || 'Error al eliminar ficha');
    }
  };
  
  const handleFileRemove = async (fichaId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await fetch(`${API_BASE}/api/muestras/fichas/${fichaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ archivo: null })
      });
      toast.success('Archivo eliminado');
      // Actualizar estado local inmediatamente
      setLocalFichas(prev => prev.map(f => f.id_ficha === fichaId ? { ...f, archivo: null } : f));
      onUpdate();
    } catch (e) { 
      toast.error('Error al eliminar archivo'); 
    }
  };

  const getFileColor = (filename) => {
    const ext = filename?.split('.').pop()?.toUpperCase();
    const colors = {
      'PDF': 'bg-red-100 text-red-700 border-red-200',
      'XLSX': 'bg-green-100 text-green-700 border-green-200',
      'XLS': 'bg-green-100 text-green-700 border-green-200',
      'DOC': 'bg-blue-100 text-blue-700 border-blue-200',
      'DOCX': 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return colors[ext] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Fichas de Base #{baseId}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-auto">
          {/* Botón crear */}
          <button
            onClick={() => setShowForm(!showForm)}
            className="mb-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Crear Nueva Ficha
          </button>

          {/* Formulario expandible */}
          {showForm && (
            <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Ficha *</label>
                  <input
                    type="text"
                    value={formData.nombre_ficha}
                    onChange={(e) => setFormData({ ...formData, nombre_ficha: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Ej: Ficha de Medidas"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.xls,.doc,.docx"
                      onChange={(e) => setArchivoFile(e.target.files[0])}
                      className="hidden"
                      id="ficha-file"
                    />
                    <label
                      htmlFor="ficha-file"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 text-sm"
                    >
                      <Upload className="w-4 h-4 text-gray-400" />
                      {archivoFile ? archivoFile.name : 'Seleccionar archivo'}
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
                >
                  {submitting ? 'Guardando...' : 'Guardar Ficha'}
                </button>
                <button
                  onClick={() => { setShowForm(false); setFormData({ nombre_ficha: '' }); setArchivoFile(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Buscador */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>

          {/* Tabla de fichas */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Nombre de Ficha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Archivo</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredFichas.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500">No hay fichas</td></tr>
                ) : (
                  filteredFichas.map((ficha, idx) => (
                    <tr key={ficha.id_ficha} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{ficha.nombre_ficha || 'Sin nombre'}</td>
                      <td className="px-4 py-3">
                        {ficha.archivo ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={`${API_BASE}/api/muestras/files/${ficha.archivo}?download=true`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getFileColor(ficha.archivo)}`}
                            >
                              <Download className="w-3 h-3" />
                              {ficha.archivo.split('.').pop()?.toUpperCase()}
                            </a>
                            <button
                              onClick={() => handleFileRemove(ficha.id_ficha)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg cursor-pointer text-xs font-medium transition-colors">
                            <Upload className="w-3 h-3" />
                            Subir
                            <input
                              type="file"
                              accept=".pdf,.xlsx,.xls,.doc,.docx"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const uploadData = new FormData();
                                  uploadData.append('file', file);
                                  const res = await fetch(`${API_BASE}/api/muestras/upload?folder=fichas`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: uploadData
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    await fetch(`${API_BASE}/api/muestras/fichas/${ficha.id_ficha}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                      body: JSON.stringify({ archivo: data.filename })
                                    });
                                    toast.success('Archivo subido');
                                    // Actualizar estado local inmediatamente
                                    setLocalFichas(prev => prev.map(f => f.id_ficha === ficha.id_ficha ? { ...f, archivo: data.filename } : f));
                                    onUpdate();
                                  } else {
                                    toast.error('Error al subir archivo');
                                  }
                                } catch (err) {
                                  toast.error('Error al subir archivo');
                                }
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(ficha.id_ficha)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MODAL DE TIZADOS
// ============================================================
const TizadosModal = ({ baseId, tizados: initialTizados, onClose, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ancho: '', curva: '' });
  const [archivoFile, setArchivoFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [submitting, setSubmitting] = useState(false);
  const [localTizados, setLocalTizados] = useState(initialTizados.filter(t => t.id_base === baseId));
  const { token } = useApp();

  // Sincronizar tizados locales cuando cambian las props
  useEffect(() => {
    setLocalTizados(initialTizados.filter(t => t.id_base === baseId));
  }, [initialTizados, baseId]);
  
  // Filtrado y ordenamiento
  const filteredTizados = useMemo(() => {
    let result = localTizados;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.ancho?.toString().includes(q) || t.curva?.toLowerCase().includes(q)
      );
    }
    if (sortField) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
    }
    return result;
  }, [localTizados, searchQuery, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let filename = null;
      if (archivoFile) {
        const uploadData = new FormData();
        uploadData.append('file', archivoFile);
        const res = await fetch(`${API_BASE}/api/muestras/upload?folder=tizados`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: uploadData
        });
        if (res.ok) {
          const data = await res.json();
          filename = data.filename;
        }
      }
      const newTizado = await createTizado({ id_base: baseId, ancho: formData.ancho, curva: formData.curva, archivo_tizado: filename });
      toast.success('Tizado creado');
      // Actualizar estado local inmediatamente
      setLocalTizados(prev => [...prev, newTizado]);
      setFormData({ ancho: '', curva: '' });
      setArchivoFile(null);
      setShowForm(false);
      onUpdate();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tizado?')) return;
    try {
      await deleteTizado(id);
      toast.success('Tizado eliminado');
      // Actualizar estado local inmediatamente
      setLocalTizados(prev => prev.filter(t => t.id_tizado !== id));
      onUpdate();
    } catch (error) {
      console.error('Error al eliminar tizado:', error);
      toast.error(error.message || 'Error al eliminar tizado');
    }
  };

  const handleFileRemove = async (tizadoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await fetch(`${API_BASE}/api/muestras/tizados/${tizadoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ archivo_tizado: null })
      });
      toast.success('Archivo eliminado');
      // Actualizar estado local inmediatamente
      setLocalTizados(prev => prev.map(t => t.id_tizado === tizadoId ? { ...t, archivo_tizado: null } : t));
      onUpdate();
    } catch (e) { 
      toast.error('Error al eliminar archivo'); 
    }
  };

  const getFileColor = (filename) => {
    const ext = filename?.split('.').pop()?.toUpperCase();
    const colors = {
      'PDF': 'bg-red-100 text-red-700 border-red-200',
      'DXF': 'bg-purple-100 text-purple-700 border-purple-200',
      'AI': 'bg-orange-100 text-orange-700 border-orange-200',
      'CDR': 'bg-pink-100 text-pink-700 border-pink-200',
      'PLT': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    };
    return colors[ext] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Tizados de Base #{baseId}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-auto">
          {/* Botón crear */}
          <button
            onClick={() => setShowForm(!showForm)}
            className="mb-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Crear Nuevo Tizado
          </button>

          {/* Formulario expandible */}
          {showForm && (
            <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho</label>
                  <input
                    type="text"
                    value={formData.ancho}
                    onChange={(e) => setFormData({ ...formData, ancho: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Ej: 150"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Curva</label>
                  <input
                    type="text"
                    value={formData.curva}
                    onChange={(e) => setFormData({ ...formData, curva: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Ej: S-M-L-XL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.dxf,.ai,.cdr,.plt"
                      onChange={(e) => setArchivoFile(e.target.files[0])}
                      className="hidden"
                      id="tizado-file"
                    />
                    <label
                      htmlFor="tizado-file"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 text-sm"
                    >
                      <Upload className="w-4 h-4 text-gray-400" />
                      {archivoFile ? archivoFile.name : 'Seleccionar'}
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
                >
                  {submitting ? 'Guardando...' : 'Guardar Tizado'}
                </button>
                <button
                  onClick={() => { setShowForm(false); setFormData({ ancho: '', curva: '' }); setArchivoFile(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Buscador */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por ancho o curva..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>

          {/* Tabla de tizados */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('ancho')}
                  >
                    <div className="flex items-center gap-1">
                      Ancho
                      {sortField === 'ancho' && (sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('curva')}
                  >
                    <div className="flex items-center gap-1">
                      Curva
                      {sortField === 'curva' && (sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Archivo</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTizados.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No hay tizados</td></tr>
                ) : (
                  filteredTizados.map((tizado) => (
                    <tr key={tizado.id_tizado} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm text-gray-500">{tizado.id_tizado}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{tizado.ancho || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{tizado.curva || '-'}</td>
                      <td className="px-4 py-3">
                        {tizado.archivo_tizado ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={`${API_BASE}/api/muestras/files/${tizado.archivo_tizado}?download=true`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getFileColor(tizado.archivo_tizado)}`}
                            >
                              <Download className="w-3 h-3" />
                              {tizado.archivo_tizado.split('.').pop()?.toUpperCase()}
                            </a>
                            <button
                              onClick={() => handleFileRemove(tizado.id_tizado)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg cursor-pointer text-xs font-medium transition-colors">
                            <Upload className="w-3 h-3" />
                            Subir
                            <input
                              type="file"
                              accept=".pdf,.dxf,.ai,.cdr,.plt"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const uploadData = new FormData();
                                  uploadData.append('file', file);
                                  const res = await fetch(`${API_BASE}/api/muestras/upload?folder=tizados`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: uploadData
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    await fetch(`${API_BASE}/api/muestras/tizados/${tizado.id_tizado}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                      body: JSON.stringify({ archivo_tizado: data.filename })
                                    });
                                    toast.success('Archivo subido');
                                    // Actualizar estado local inmediatamente
                                    setLocalTizados(prev => prev.map(t => t.id_tizado === tizado.id_tizado ? { ...t, archivo_tizado: data.filename } : t));
                                    onUpdate();
                                  } else {
                                    toast.error('Error al subir archivo');
                                  }
                                } catch (err) {
                                  toast.error('Error al subir archivo');
                                }
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(tizado.id_tizado)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL: BASES
// ============================================================
const MuestrasBases = ({ muestrasUser, muestrasIsAdmin }) => {
  const { user, token } = useApp();
  // Importamos el hook de permisos aquí también
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Bases
  const puedeCrear = canCreate('bases');
  const puedeEditar = canEdit('bases');
  const puedeEliminar = canDelete('bases');

  // Hook para columnas redimensionables
  const { columnWidths, handleResize, resetWidths } = useResizableColumns(INITIAL_BASES_COLUMNS, 'muestras-bases');

  const [data, setData] = useState([]);
  const [muestrasBase, setMuestrasBase] = useState([]);
  const [tizados, setTizados] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('aprobados');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBase, setEditingBase] = useState(null);
  const [formData, setFormData] = useState({ id_muestra_base: '', aprobado: 0 });
  const [imagenFile, setImagenFile] = useState(null);
  const [patronFile, setPatronFile] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fichasModalBase, setFichasModalBase] = useState(null);
  const [tizadosModalBase, setTizadosModalBase] = useState(null);

  // Catálogos
  const [catalogos, setCatalogos] = useState({ marcas: [], tipos: [], entalles: [], telas: [], hilos: [] });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [basesData, muestrasData, tizadosData, fichasData, marcasData, tiposData, entallesData, telasData, hilosData] = await Promise.all([
        getBases(), getMuestrasBase(), getTizados(), getFichas(),
        getMarcas(), getTiposProducto(), getEntalles(), getTelas(), getHilos()
      ]);
      setData(basesData || []);
      setMuestrasBase(muestrasData || []);
      setTizados(tizadosData || []);
      setFichas(fichasData || []);
      setCatalogos({
        marcas: marcasData || [],
        tipos: tiposData || [],
        entalles: entallesData || [],
        telas: telasData || [],
        hilos: hilosData || []
      });
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Filtrado
  const filteredData = useMemo(() => {
    let result = data;
    if (filter === 'aprobados') result = result.filter(item => item.aprobado === 1);
    else if (filter === 'pendientes') result = result.filter(item => !item.aprobado || item.aprobado === 0);
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => {
        const muestra = muestrasBase.find(m => m.id_muestra_base === item.id_muestra_base);
        const marca = muestra ? catalogos.marcas.find(x => x.id_marca === muestra.id_marca) : null;
        const tipo = muestra ? catalogos.tipos.find(x => x.id_tipo === muestra.id_tipo) : null;
        return marca?.nombre_marca?.toLowerCase().includes(q) || tipo?.nombre_tipo?.toLowerCase().includes(q);
      });
    }
    return result;
  }, [data, filter, searchQuery, muestrasBase, catalogos]);

  // Contadores
  const aprobadosCount = data.filter(item => item.aprobado === 1).length;
  const pendientesCount = data.filter(item => !item.aprobado || item.aprobado === 0).length;

  // Helper para mostrar "Marca - Tipo - Entalle - Tela - Hilo"
  const getMuestraLabel = (idMuestra) => {
    const m = muestrasBase.find(x => x.id_muestra_base === idMuestra);
    if (!m) return '-';
    const marca = catalogos.marcas.find(x => x.id_marca === m.id_marca);
    const tipo = catalogos.tipos.find(x => x.id_tipo === m.id_tipo);
    const entalle = catalogos.entalles.find(x => x.id_entalle === m.id_entalle);
    const tela = catalogos.telas.find(x => x.id_tela === m.id_tela);
    const hilo = catalogos.hilos.find(x => x.id_hilo === m.id_hilo);
    const parts = [marca?.nombre_marca, tipo?.nombre_tipo, entalle?.nombre, tela?.nombre, hilo?.codigo || hilo?.nombre].filter(Boolean);
    return parts.join(' - ') || 'Sin datos';
  };

  // Opciones para selector
  const muestraOptions = useMemo(() => {
    return muestrasBase.map(m => ({
      value: m.id_muestra_base,
      label: `#${m.id_muestra_base} - ${getMuestraLabel(m.id_muestra_base)}`
    }));
  }, [muestrasBase, catalogos]);

  const openModal = (base = null) => {
    setEditingBase(base);
    setFormData({ id_muestra_base: base?.id_muestra_base || '', aprobado: base?.aprobado || 0 });
    setImagenFile(null);
    setPatronFile(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.id_muestra_base) { toast.error('La muestra base es requerida'); return; }
    try {
      let baseId;
      if (editingBase) {
        await updateBase(editingBase.id_base, formData);
        baseId = editingBase.id_base;
        toast.success('Base actualizada');
      } else {
        const result = await createBase(formData);
        baseId = result.id_base;
        toast.success('Base creada');
      }
      if (imagenFile && baseId) await uploadBaseFile(baseId, 'imagen', imagenFile);
      if (patronFile && baseId) await uploadBaseFile(baseId, 'patron', patronFile);
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteBase(deleteItem.id_base);
      toast.success('Base eliminada');
      setDeleteItem(null);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  // Handlers para subida de archivos en celda
  const handleUploadPatron = async (baseId, file) => {
    await uploadBaseFile(baseId, 'patron', file);
    loadData();
    toast.success('Patrón subido');
  };

  const handleRemovePatron = async (baseId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/muestras/bases/${baseId}/patron`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar');
      loadData();
      toast.success('Patrón eliminado');
    } catch (error) {
      toast.error(error.message || 'Error al eliminar patrón');
    }
  };

  const handleUploadImagen = async (baseId, file) => {
    await uploadBaseFile(baseId, 'imagen', file);
    loadData();
    toast.success('Imagen subida');
  };

  const handleRemoveImagen = async (baseId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/muestras/bases/${baseId}/imagen`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar');
      loadData();
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error(error.message || 'Error al eliminar imagen');
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-bases-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 rounded-xl">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Bases</h1>
            <p className="text-gray-400 text-sm">Desarrollo de modelos y patrones</p>
          </div>
        </div>
        {puedeCrear && (
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <button
            onClick={() => setFilter('aprobados')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'aprobados' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-green-50'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Aprobados ({aprobadosCount})
          </button>
          <button
            onClick={() => setFilter('pendientes')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pendientes' ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-slate-50'
            }`}
          >
            <XCircle className="w-4 h-4" />
            Pendientes ({pendientesCount})
          </button>
          <button
            onClick={() => setFilter('todos')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'todos' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-blue-50'
            }`}
          >
            Todos ({data.length})
          </button>
        </div>
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Botón para resetear anchos */}
        <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
          <span className="text-xs text-gray-500">Arrastra los bordes de las columnas para ajustar el ancho</span>
          <button
            onClick={resetWidths}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            title="Restaurar anchos por defecto"
          >
            <RotateCcw className="w-3 h-3" />
            Restaurar
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filteredData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay bases</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-slate-100 border-b">
                <tr>
                  <ResizableHeader
                    width={columnWidths.muestra}
                    onResize={(w) => handleResize('muestra', w)}
                    minWidth={150}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Muestra Base
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.patron}
                    onResize={(w) => handleResize('patron', w)}
                    minWidth={90}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Patrón
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.imagen}
                    onResize={(w) => handleResize('imagen', w)}
                    minWidth={80}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Imagen
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.fichas}
                    onResize={(w) => handleResize('fichas', w)}
                    minWidth={70}
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                  >
                    Fichas
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.tizados}
                    onResize={(w) => handleResize('tizados', w)}
                    minWidth={70}
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                  >
                    Tizados
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.estado}
                    onResize={(w) => handleResize('estado', w)}
                    minWidth={70}
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                  >
                    Estado
                  </ResizableHeader>
                  {(puedeEditar || puedeEliminar) && (
                    <ResizableHeader
                      width={columnWidths.acciones}
                      onResize={(w) => handleResize('acciones', w)}
                      minWidth={80}
                      className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                    >
                      Acciones
                    </ResizableHeader>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((base) => {
                  const baseFichas = fichas.filter(f => f.id_base === base.id_base);
                  const baseTizados = tizados.filter(t => t.id_base === base.id_base);
                  
                  return (
                    <tr key={base.id_base} className="border-b hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3" style={{ width: columnWidths.muestra }}>
                        <div className="flex items-center gap-2 truncate">
                          <span className="font-mono text-xs text-gray-400">#{base.id_base}</span>
                          <span className="font-medium text-gray-900 truncate">{getMuestraLabel(base.id_muestra_base)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ width: columnWidths.patron }}>
                        <FileCell
                          value={base.patron}
                          onUpload={(file) => handleUploadPatron(base.id_base, file)}
                          onRemove={() => handleRemovePatron(base.id_base)}
                          accept=".pdf,.dxf,.ai,.cdr,.plt"
                          disabled={!puedeEditar}
                        />
                      </td>
                      <td className="px-4 py-3" style={{ width: columnWidths.imagen }}>
                        <ImageCell
                          value={base.imagen}
                          onUpload={(file) => handleUploadImagen(base.id_base, file)}
                          onRemove={() => handleRemoveImagen(base.id_base)}
                          disabled={!puedeEditar}
                        />
                      </td>
                      <td className="px-4 py-3 text-center" style={{ width: columnWidths.fichas }}>
                        <button
                          onClick={() => setFichasModalBase(base)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            baseFichas.length > 0
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          <FileText className="w-3 h-3" />
                          {baseFichas.length > 0 ? `${baseFichas.length} fichas` : '+ Agregar'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center" style={{ width: columnWidths.tizados }}>
                        <button
                          onClick={() => setTizadosModalBase(base)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            baseTizados.length > 0
                              ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          <Scissors className="w-3 h-3" />
                          {baseTizados.length > 0 ? `${baseTizados.length} tizados` : '+ Agregar'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center" style={{ width: columnWidths.estado }}>
                        <StatusBadge aprobado={base.aprobado} />
                      </td>
                      {(puedeEditar || puedeEliminar) && (
                        <td className="px-4 py-3" style={{ width: columnWidths.acciones }}>
                          <div className="flex items-center justify-center gap-1">
                            {puedeEditar && (
                              <button onClick={() => openModal(base)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                            {puedeEliminar && (
                              <button onClick={() => setDeleteItem(base)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{editingBase ? `Editar Base #${editingBase.id_base}` : 'Nueva Base'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Datos básicos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Muestra Base *</label>
                  <select
                    value={formData.id_muestra_base}
                    onChange={(e) => setFormData({ ...formData, id_muestra_base: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar muestra</option>
                    {muestraOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Estado:</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, aprobado: formData.aprobado === 1 ? 0 : 1 })}
                    className={`relative w-11 h-6 rounded-full transition-colors ${formData.aprobado === 1 ? 'bg-blue-500' : 'bg-gray-200'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.aprobado === 1 ? 'translate-x-5' : ''}`} />
                  </button>
                  <span className="text-sm text-gray-600">{formData.aprobado === 1 ? 'Aprobado' : 'Pendiente'}</span>
                </div>
              </div>

              {/* Archivos: Patrón e Imagen */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Patrón</label>
                  {editingBase?.patron ? (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {editingBase.patron.split('/').pop()?.split('_').slice(1).join('_') || 'archivo.pdf'}
                      </span>
                      <a
                        href={`${API_BASE}/api/muestras/files/${editingBase.patron}?download=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={async () => {
                          if (window.confirm('¿Eliminar patrón?')) {
                            await handleRemovePatron(editingBase.id_base);
                            setEditingBase({ ...editingBase, patron: null });
                          }
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 cursor-pointer">
                      <input type="file" accept=".pdf,.dxf,.ai,.cdr" onChange={(e) => setPatronFile(e.target.files[0])} className="hidden" id="modal-patron" />
                      <label htmlFor="modal-patron" className="cursor-pointer">
                        <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500 truncate">{patronFile ? patronFile.name : 'Subir patrón'}</p>
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagen</label>
                  {editingBase?.imagen ? (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <img
                        src={`${API_BASE}/api/muestras/files/${editingBase.imagen}`}
                        alt="Preview"
                        className="w-10 h-10 object-cover rounded"
                      />
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {editingBase.imagen.split('/').pop()?.split('_').slice(1).join('_') || 'imagen.jpg'}
                      </span>
                      <a
                        href={`${API_BASE}/api/muestras/files/${editingBase.imagen}?download=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={async () => {
                          if (window.confirm('¿Eliminar imagen?')) {
                            await handleRemoveImagen(editingBase.id_base);
                            setEditingBase({ ...editingBase, imagen: null });
                          }
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 cursor-pointer">
                      <input type="file" accept="image/*" onChange={(e) => setImagenFile(e.target.files[0])} className="hidden" id="modal-imagen" />
                      <label htmlFor="modal-imagen" className="cursor-pointer">
                        <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500 truncate">{imagenFile ? imagenFile.name : 'Subir imagen'}</p>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Fichas - Solo en modo edición */}
              {editingBase && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Fichas Técnicas</h4>
                    <span className="text-xs text-gray-400">{fichas.filter(f => f.id_base === editingBase.id_base).length} fichas</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Nombre</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Archivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fichas.filter(f => f.id_base === editingBase.id_base).length === 0 ? (
                          <tr><td colSpan="3" className="px-3 py-4 text-center text-gray-400 text-xs">Sin fichas</td></tr>
                        ) : (
                          fichas.filter(f => f.id_base === editingBase.id_base).map((ficha, idx) => (
                            <tr key={ficha.id_ficha} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                              <td className="px-3 py-2 text-gray-800">{ficha.nombre_ficha || '-'}</td>
                              <td className="px-3 py-2">
                                {ficha.archivo ? (
                                  <a
                                    href={`${API_BASE}/api/muestras/files/${ficha.archivo}?download=true`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs"
                                  >
                                    <Download className="w-3 h-3" />
                                    {ficha.archivo.split('.').pop()?.toUpperCase()}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={() => { setShowModal(false); setFichasModalBase(editingBase); }}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    Gestionar fichas →
                  </button>
                </div>
              )}

              {/* Tizados - Solo en modo edición */}
              {editingBase && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Tizados</h4>
                    <span className="text-xs text-gray-400">{tizados.filter(t => t.id_base === editingBase.id_base).length} tizados</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">ID</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Ancho</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Curva</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Archivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tizados.filter(t => t.id_base === editingBase.id_base).length === 0 ? (
                          <tr><td colSpan="4" className="px-3 py-4 text-center text-gray-400 text-xs">Sin tizados</td></tr>
                        ) : (
                          tizados.filter(t => t.id_base === editingBase.id_base).map((tizado) => (
                            <tr key={tizado.id_tizado} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono text-gray-500">{tizado.id_tizado}</td>
                              <td className="px-3 py-2 text-gray-800">{tizado.ancho || '-'}</td>
                              <td className="px-3 py-2 text-gray-700">{tizado.curva || '-'}</td>
                              <td className="px-3 py-2">
                                {tizado.archivo_tizado ? (
                                  <a
                                    href={`${API_BASE}/api/muestras/files/${tizado.archivo_tizado}?download=true`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-xs"
                                  >
                                    <Download className="w-3 h-3" />
                                    {tizado.archivo_tizado.split('.').pop()?.toUpperCase()}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={() => { setShowModal(false); setTizadosModalBase(editingBase); }}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    Gestionar tizados →
                  </button>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">
                Cancelar
              </button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium">
                {editingBase ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmación Eliminar */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Base?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar la base <strong>#{deleteItem.id_base}</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteItem(null)} disabled={deleting} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">
                  Cancelar
                </button>
                <button onClick={confirmDelete} disabled={deleting} className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50">
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modales de Fichas y Tizados */}
      {fichasModalBase && (
        <FichasModal
          baseId={fichasModalBase.id_base}
          fichas={fichas}
          onClose={() => setFichasModalBase(null)}
          onUpdate={loadData}
        />
      )}
      {tizadosModalBase && (
        <TizadosModal
          baseId={tizadosModalBase.id_base}
          tizados={tizados}
          onClose={() => setTizadosModalBase(null)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default MuestrasBases;
